import { defineStore } from '@repo/stores';
import type { FabricDoc, MetadataProperty, MetaInfo, Palette } from '@repo/style3d';
import { computed, ref, toRaw, watch } from 'vue';
import { rgbToHex, setWindowTitle } from '@repo/utils';
import { getAppConfig } from '@/utils';
import { useRecentFabricListStore } from '@/store/recent-fabric-list.ts';
import { useParamStore } from '@/store/param.ts';

const { DEFAULT_COMPANY_NAME } = getAppConfig();

export interface FabricInfo {
  id: string;
  originUrl?: string;
  fabricId: string;
  info: MetaInfo;
  thumb: string;
  xhr_path?: string;
  enterpriseId: string;
  enterpriseName: string;
  resourceType?: 'cloudknit' | 'style3d';
  properties: Record<string, string>[];
  styleProperties?: Record<string, string>[];
  carousels?: (FabricDoc & { url: string })[];
  logo?: string;
  mask: {
    srcImage?:string;
    colors?:string[];
    list: string[];
  };
}

export interface ColorPalette extends Omit<Palette, 'color'> {
  color: string;
}

export const useFabricStore = defineStore('fabric', () => {
  const fabricInfo = ref<FabricInfo>();
  // 存储多个面料信息，key为materialIndex
  const fabricInfoMap = ref<Map<number, FabricInfo>>(new Map());
  const recentFabricStore = useRecentFabricListStore();
  const paramStore = useParamStore();

  const fabricProperty = computed(() => {
    return fabricInfo.value?.properties || [];
  });

  const styleProperty = computed(() => {
    return fabricInfo.value?.styleProperties || [];
  });

  const palette = computed<ColorPalette[] | undefined>(() => {
    return fabricInfo.value?.info?.palette?.map(item => ({
      ...item,
      color: rgbToHex(item.color.R, item.color.G, item.color.B)
    }));
  });

  const setFabricInfo = async (val: FabricInfo, materialIndex?: number) => {
    // 为了避免引用问题，创建深拷贝
    const clonedVal = {
      ...val,
      info: { ...val.info },
      mask: {
        ...val.mask,
        colors: val.mask.colors ? [...val.mask.colors] : undefined,
        list: [...val.mask.list]
      },
      properties: [...val.properties],
      styleProperties: val.styleProperties ? [...val.styleProperties] : undefined,
      carousels: val.carousels ? [...val.carousels] : undefined
    };
    
    // 如果提供了materialIndex，存储到map中
    if (materialIndex !== undefined) {
      fabricInfoMap.value.set(materialIndex, clonedVal);
      
      // 如果是materialIndex=0，同时更新主fabricInfo
      if (materialIndex === 0) {
        fabricInfo.value = clonedVal;
      }
    } else {
      // 没有指定materialIndex时，只更新主fabricInfo（向后兼容）
      fabricInfo.value = clonedVal;
    }

    const {
      id,
      fabricId,
      originUrl,
      thumb,
      enterpriseId,
      enterpriseName,
      info,
      resourceType = 'cloudknit',
      properties,
      mask
    } = val;

    await recentFabricStore.addRecentFabric({
      id,
      fabricId,
      originUrl,
      resourceType,
      info: toRaw(info),
      thumb: thumb || '',
      enterpriseId,
      enterpriseName,
      properties: toRaw(properties || []),
      mask
    });
  };

  const setFabricProperties = (val: MetadataProperty['data']) => {
    if (fabricInfo.value) {
      fabricInfo.value.properties = [...val.map(toRaw)];
    }
  };

  const setFabricThumb = (url: string) => {
    if (fabricInfo.value) {
      fabricInfo.value.thumb = url;
    }
  };

  watch(() => fabricInfo.value?.enterpriseName, (val) => {
    let title = val || paramStore.dispalyname || DEFAULT_COMPANY_NAME;
    setWindowTitle(title);

    // 模拟一次隐藏再显示，迫使 WebView 刷新标题栏
    Object.defineProperty(document, 'hidden', { value: true });
    document.dispatchEvent(new Event('visibilitychange'));
    setTimeout(() => {
      Object.defineProperty(document, 'hidden', { value: false });
      document.dispatchEvent(new Event('visibilitychange'));
    }, 100);

    // const titleEl = document.querySelector('title');
    // if (titleEl) {
    //   titleEl.innerHTML = title;
    // } else {
    //   const newTitle = document.createElement('title');
    //   newTitle.textContent = title;
    //   document.head.appendChild(newTitle);
    // }

    // // 先判断是否钉钉环境、dd是否存在
    // const ua = navigator.userAgent.toLowerCase();
    // if (!ua.includes('dingtalk')) {
    //   //document.title = title;
    //   return;
    // }
//  // 轮询等待window.dd就绪，解决冷启动延迟
//     let retry = 0;
//     const maxRetry = 5;
//     const delayMs = 200;

//     function updateDingNavTitle() {
//       if (window.dd) {
//         // dd.ready只会执行一次，安全包裹所有钉钉API
//         window.dd.ready(() => {
//           window.dd.biz.navigation.setTitle({
//             title,
//             onSuccess: () => {},
//             onFail: (err) => console.warn('钉钉设置标题失败', err)
//           });
//         });
//         return;
//       }
//       if (retry < maxRetry) {
//         retry++;
//         setTimeout(updateDingNavTitle, delayMs);
//       }
//     }
//     updateDingNavTitle();
  });

  // 选中的区域颜色索引（用于多区域变色）
  // 为每个materialIndex保存独立的选中区域索引
  const selectedRegionIndexMap = ref<Map<number, number>>(new Map());
  
  // 向后兼容的单一索引（用于materialIndex=0或未指定的情况）
  const selectedRegionIndex = ref<number>(0);

  const setSelectedRegionIndex = (index: number, materialIndex?: number) => {
    if (materialIndex !== undefined) {
      selectedRegionIndexMap.value.set(materialIndex, index);
      // 如果是materialIndex=0，同时更新主索引
      if (materialIndex === 0) {
        selectedRegionIndex.value = index;
      }
    } else {
      // 向后兼容：未指定materialIndex时更新主索引
      selectedRegionIndex.value = index;
      selectedRegionIndexMap.value.set(0, index);
    }
  };
  
  const getSelectedRegionIndex = (materialIndex?: number): number => {
    if (materialIndex !== undefined) {
      // 获取指定materialIndex的选中索引，如果不存在则返回0
      return selectedRegionIndexMap.value.get(materialIndex) ?? 0;
    }
    // 向后兼容：未指定materialIndex时返回主索引
    return selectedRegionIndex.value;
  };

  const updateRegionColor = (index: number, color: string, materialIndex?: number) => {
    if (materialIndex !== undefined) {
      // 只更新指定materialIndex的fabricInfo
      const targetFabricInfo = fabricInfoMap.value.get(materialIndex);
      if (targetFabricInfo?.mask?.colors && index >= 0 && index < targetFabricInfo.mask.colors.length) {
        targetFabricInfo.mask.colors[index] = color;
      }
    } else {
      // 没有指定materialIndex时，更新主fabricInfo（向后兼容）
      if (fabricInfo.value?.mask?.colors && index >= 0 && index < fabricInfo.value.mask.colors.length) {
        fabricInfo.value.mask.colors[index] = color;
      }
      // 如果主fabricInfo也在map中（materialIndex=0），同步更新
      const mainFabricInfo = fabricInfoMap.value.get(0);
      if (mainFabricInfo?.mask?.colors && index >= 0 && index < mainFabricInfo.mask.colors.length) {
        mainFabricInfo.mask.colors[index] = color;
      }
    }
  };

  // 根据materialIndex获取面料信息
  const getFabricInfoByMaterialIndex = (materialIndex: number): FabricInfo | undefined => {
    return fabricInfoMap.value.get(materialIndex);
  };

  // 清空指定materialIndex的面料信息
  const clearFabricInfo = (materialIndex?: number) => {
    if (materialIndex !== undefined) {
      fabricInfoMap.value.delete(materialIndex);
    } else {
      fabricInfoMap.value.clear();
    }
  };

  return { 
    fabricInfo, 
    fabricInfoMap,
    fabricProperty, 
    styleProperty, 
    setFabricInfo, 
    setFabricProperties, 
    setFabricThumb, 
    palette, 
    selectedRegionIndex, 
    setSelectedRegionIndex,
    getSelectedRegionIndex,
    updateRegionColor,
    getFabricInfoByMaterialIndex,
    clearFabricInfo
  };
});
